import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';

const STORAGE_KEY = 'current_order_id';
const STORAGE_CLIENT_KEY = 'current_order_client_id';

export const useOrder = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const clientIdParam = searchParams.get('clientId');

    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [autoSaving, setAutoSaving] = useState(false);
    const [orderItems, setOrderItems] = useState([]);

    const fetchOrderItems = useCallback(async (orderId) => {
        if (!orderId) return;
        try {
            setLoading(true);
            const response = await axios.get(`/api/orders/${orderId}/`);
            setOrderItems(response.data.items || []);
            setOrder(response.data); // Update full order to get totals
        } catch (error) {
            console.error('Error fetching items:', error);
            setError('Error al actualizar items');
        } finally {
            setLoading(false);
        }
    }, []);

    const createOrder = useCallback(async (clientId, specialProduction, deliveryDate, orderType = 'retail') => {
        try {
            setLoading(true);
            const orderData = {
                client: clientId ? parseInt(clientId) : null,
                client_id: clientId ? parseInt(clientId) : null,
                status: 'borrador',
                special_production: specialProduction,
                delivery_date: deliveryDate || null,
                order_type: orderType === 'business' ? 'wholesale' : 'retail',
            };

            const response = await axios.post('/api/orders/', orderData);
            const newOrder = response.data;

            setOrder(newOrder);
            localStorage.setItem(STORAGE_KEY, newOrder.id.toString());
            if (clientId) {
                localStorage.setItem(STORAGE_CLIENT_KEY, clientId);
            }
            return newOrder;
        } catch (err) {
            setError(err.response?.data?.error || 'Error al crear pedido');
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const restoreOrCreateOrder = useCallback(async (orderType = 'retail') => {
        setLoading(true);
        try {
            const savedOrderId = localStorage.getItem(STORAGE_KEY);
            const savedClientId = localStorage.getItem(STORAGE_CLIENT_KEY);
            const effectiveClientId = clientIdParam || (savedClientId && savedClientId !== 'null' && savedClientId !== 'undefined' ? savedClientId : null);

            if (savedOrderId) {
                try {
                    const response = await axios.get(`/api/orders/${savedOrderId}/`);
                    const savedOrder = response.data;

                    const expectedOrderType = orderType === 'business' ? 'wholesale' : 'retail';

                    if (savedOrder.status === 'borrador' && savedOrder.order_type === expectedOrderType) {
                        setOrder(savedOrder);
                        setOrderItems(savedOrder.items || []);

                        // Update client if URL param differs from current client
                        const currentClientId = savedOrder.client?.id;
                        if (clientIdParam && currentClientId !== parseInt(clientIdParam)) {
                            await axios.patch(`/api/orders/${savedOrderId}/`, { 
                                client: parseInt(clientIdParam),
                                client_id: parseInt(clientIdParam) 
                            });
                            const updated = await axios.get(`/api/orders/${savedOrderId}/`);
                            setOrder(updated.data);
                        }
                        return;
                    } else {
                        localStorage.removeItem(STORAGE_KEY);
                        localStorage.removeItem(STORAGE_CLIENT_KEY);
                    }
                } catch (e) {
                    localStorage.removeItem(STORAGE_KEY);
                }
            }

            await createOrder(effectiveClientId, false, null, orderType);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [clientIdParam, createOrder]);

    const addItem = async (productId, quantity, unitPrice = null) => {
        if (!order) return;
        try {
            setError('');
            await axios.post(`/api/orders/${order.id}/add_item/`, {
                product_id: productId,
                quantity: parseFloat(quantity),
                unit_price: unitPrice
            });
            await fetchOrderItems(order.id);
            setSuccess('Producto agregado');
            setTimeout(() => setSuccess(''), 2000);
        } catch (err) {
            setError(err.response?.data?.error || 'Error al agregar producto');
        }
    };

    const updateItem = async (itemId, data) => {
        if (!order) return;
        try {
            await axios.put(`/api/orders/${order.id}/update_item/`, {
                item_id: itemId,
                ...data
            });
            await fetchOrderItems(order.id);
        } catch (err) {
            setError('Error al actualizar item');
        }
    };

    const removeItem = async (itemId) => {
        if (!order) return;
        try {
            await axios.delete(`/api/orders/${order.id}/remove_item/?item_id=${itemId}`);
            await fetchOrderItems(order.id);
        } catch (err) {
            setError('Error al eliminar item');
        }
    };

    const updateOrder = async (data) => {
        if (!order) return;
        try {
            const response = await axios.patch(`/api/orders/${order.id}/`, data);
            setOrder(response.data);
        } catch (err) {
            console.error('Error updating order:', err);
            // Don't set global error for background updates unless critical
        }
    };

    const validateOrder = async (userId, password) => {
        if (!order) return;
        try {
            await axios.post(`/api/orders/${order.id}/validate_order/`, {
                user_id: userId,
                password
            });
            localStorage.removeItem(STORAGE_KEY);
            localStorage.removeItem(STORAGE_CLIENT_KEY);
            navigate(`/orders/${order.id}/review`);
        } catch (err) {
            setError(err.response?.data?.error || 'Error de validación');
            throw err;
        }
    };

    // Auto-save effect
    useEffect(() => {
        // Solo para pedidos en borrador: si el pedido ya fue confirmado/facturado/etc.
        // (ej: al editarlo desde producción) NO debemos revertirlo a 'borrador'.
        if (!order || !order.id || order.status !== 'borrador') return;
        const interval = setInterval(async () => {
            try {
                setAutoSaving(true);
                // Just pinging to ensure session/state? Or actually saving fields?
                // The original code patches status='borrador' as a keep-alive/save trigger
                await axios.patch(`/api/orders/${order.id}/`, { status: 'borrador' });
            } catch (e) { console.error(e); }
            finally { setTimeout(() => setAutoSaving(false), 1000); }
        }, 30000);
        return () => clearInterval(interval);
    }, [order]);

    const validateResponsible = async (userId, password) => {
        if (!order) return;
        try {
            await axios.post(`/api/orders/${order.id}/validate_responsible/`, {
                user_id: parseInt(userId),
                password
            });
            return true;
        } catch (err) {
            setError(err.response?.data?.error || 'Error de validación de responsable');
            return false;
        }
    };

    return {
        order,
        orderItems,
        loading,
        error,
        success,
        autoSaving,
        restoreOrCreateOrder,
        addItem,
        updateItem,
        removeItem,
        updateOrder,
        validateOrder,
        validateResponsible,
        setError,
        setSuccess,
        fetchOrder: fetchOrderItems
    };
};
